import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import FiyrPodLogo from '../../../logos/fiyrpod-logo-text.png';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Drawer,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import FormControlLabel from '@mui/material/FormControlLabel';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { GridColDef } from '@mui/x-data-grid';
import {
    DataGridPremium,
    GridRenderCellParams,
    GridSortItem,
    GridSortModel,
} from '@mui/x-data-grid-premium';
import { Request } from 'api';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { formatCurrency, formatDateTimeStringToLocale } from '../../../app.functions';
import CustomPagination from '../../../components/Functional/CustomPagination';
import CustomLoadingOverlay from '../../../components/Loaders/LoadingOverlay';
import PartnershipCommissionsAnalytics from '../components/PartnershipCommissionsAnalytics';
import { getCommissionTypes, getUnpaidCommissionsGroupedByPayoutDate } from '../networking.api';
import { Commission } from '../networking.types';
import { NetworkingActionTypes } from '../state/networking.state';
import { useDispatchContext, useNetworkingCommissions } from '../state/NetworkingContext';
import { Order } from '../networking.types';
import OrderView from '../components/OrderView';
import { AccessTime, AttachMoney } from '@mui/icons-material';

interface PartnershipEarningsSceneProps {
    organizationId: string;
}

const PartnershipEarningsScene: React.FC<PartnershipEarningsSceneProps> = ({ organizationId }) => {
    const commissions = useNetworkingCommissions();
    const dispatch = useDispatchContext();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedCheckMatchingPayoutUuid, setSelectedCheckMatchingPayoutUuid] = useState('');
    const [drawerCommissions, setDrawerCommissions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [commissionTypes, setCommissionTypes] = useState<string[]>([]);
    const [unpaidCommissionsGroupedByPayoutDate, setUnpaidCommissionsGroupedByPayoutDate] =
        useState<number[]>([]);
    const [selectedCommissionType, setSelectedCommissionType] = useState<string[]>([]);
    const [selectedMonth, setSelectedMonth] = useState<string>('');
    const [orderViewOpen, setOrderViewOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

    const debouncedSearchTerm = useDebounce(searchTerm, 600);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    function useDebounce(value: string, delay: number) {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => {
                clearTimeout(handler);
            };
        }, [value, delay]);

        return debouncedValue;
    }

    useEffect(() => {
        if (drawerOpen && selectedCheckMatchingPayoutUuid) {
            Request.get(
                `/organizations/${organizationId}/networking/commissions?filter[check_matching_payout_uuid]=${selectedCheckMatchingPayoutUuid}`,
            )
                .then((response) => {
                    setDrawerCommissions(response.data.data);
                })
                .catch((error: any) => {
                    // 401 is handled by the global axios interceptor (auto-logout)
                    if (error?.response?.status !== 401) {
                        console.error('There was an error!', error);
                    }
                });
        }
    }, [organizationId, drawerOpen, selectedCheckMatchingPayoutUuid, dispatch]);

    useEffect(() => {
        dispatch({
            type: NetworkingActionTypes.COMMISSIONS_FILTERS_CHANGED,
            payload: {
                page: 1,
                search: debouncedSearchTerm,
            },
        });
    }, [debouncedSearchTerm, dispatch]);

    useEffect(() => {
        const fetchCommissionTypes = async () => {
            try {
                const response = await getCommissionTypes(organizationId);
                setCommissionTypes(response.data);
            } catch (error: any) {
                // 401 is handled by the global axios interceptor (auto-logout)
                if (error?.response?.status !== 401) {
                    console.error('Failed to fetch commission types', error);
                }
            }
        };
        fetchCommissionTypes();
    }, [organizationId]);

    useEffect(() => {
        const fetchUnpaidCommissionsGroupedByPayoutDate = async () => {
            try {
                const response = await getUnpaidCommissionsGroupedByPayoutDate(organizationId);
                setUnpaidCommissionsGroupedByPayoutDate(response.data);
            } catch (error: any) {
                // 401 is handled by the global axios interceptor (auto-logout)
                if (error?.response?.status !== 401) {
                    console.error('Failed to fetch unpaid commissions', error);
                }
            }
        };
        fetchUnpaidCommissionsGroupedByPayoutDate();
    }, [organizationId]);

    useEffect(() => {
        console.log(unpaidCommissionsGroupedByPayoutDate);
    }, [unpaidCommissionsGroupedByPayoutDate]);

    const handleTypeFilterChange = (event: SelectChangeEvent<string[]>) => {
        setSelectedCommissionType(event.target.value as string[]);
        dispatch({
            type: NetworkingActionTypes.COMMISSIONS_FILTERS_CHANGED,
            payload: {
                page: 1,
                'filter[type]': event.target.value,
            },
        });
    };

    const monthOptions = useMemo(() => {
        const options: { value: string; label: string }[] = [];
        const now = DateTime.now();
        for (let i = 0; i < 12; i++) {
            const dt = now.minus({ months: i });
            options.push({
                value: dt.toFormat('yyyy-MM'),
                label: dt.toFormat('MMMM yyyy'),
            });
        }
        return options;
    }, []);

    const handleMonthFilterChange = (event: SelectChangeEvent<string>) => {
        const val = event.target.value;
        setSelectedMonth(val);
        dispatch({
            type: NetworkingActionTypes.COMMISSIONS_FILTERS_CHANGED,
            payload: {
                page: 1,
                'filter[earned_date_month]': val || undefined,
            },
        });
    };

    // Function to handle opening the order view
    const handleOpenOrderView = (order: Order) => {
        setSelectedOrder(order);
        setOrderViewOpen(true);
    };

    // Function to handle closing the order view
    const handleCloseOrderView = () => {
        setOrderViewOpen(false);
        setSelectedOrder(null);
    };

    const columns: GridColDef[] = [
        {
            field: 'type',
            headerName: 'Earnings Type',
            minWidth: 250,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                const containsCheckMatching = params.value.includes('Check Matching');
                const commission: Commission = params.row as Commission; // assuming params.row is of type Commission
                return (
                    <Box display="flex" alignItems="center">
                        <span>{params.value}</span>
                        {containsCheckMatching && commission.check_matching_payout_uuid && (
                            <IconButton
                                onClick={() => {
                                    setDrawerOpen(true);
                                    setSelectedCheckMatchingPayoutUuid(
                                        commission.check_matching_payout_uuid,
                                    );
                                }}
                            >
                                <MonetizationOnIcon />
                            </IconButton>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'amount',
            headerName: 'Amount',
            minWidth: 100,
            disableColumnMenu: true,
            sortable: true,
            renderCell: (params: GridRenderCellParams<any, number>) => {
                return formatCurrency({
                    amount: ((params.value ?? 0) / 100) as number,
                    currency: 'USD',
                });
            },
        },
        {
            field: 'order',
            headerName: 'Order',
            minWidth: 100,
            disableColumnMenu: true,
            sortable: true,
            renderCell: (params: GridRenderCellParams<any, number>) => {
                const commission = params.row as Commission;
                if (commission.order) {
                    return (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                height: '100%',
                                width: '100%',
                            }}
                        >
                            <Button
                                size="medium"
                                variant="contained"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenOrderView(commission.order as Order);
                                }}
                                title="View Order Details"
                            >
                                Order
                            </Button>
                        </Box>
                    );
                }
                return null;
            },
        },
        {
            field: 'earned_date',
            headerName: 'Earned On',
            minWidth: 200,
            disableColumnMenu: true,
            sortable: true,
            renderCell: (params: GridRenderCellParams<any, string>) => {
                return formatDateTimeStringToLocale(params.value, 'America/Los_Angeles');
            },
        },
        {
            field: 'payoutDate',
            headerName: 'Payout Date',
            minWidth: 140,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params: any) => {
                if (params.row.is_paid) {
                    return (
                        <Chip
                            icon={<AttachMoney />}
                            label={params.row.payout_date}
                            style={{ borderRadius: '4px' }}
                            size="small"
                            color="success"
                            variant="filled"
                            title="Paid out on this date"
                        />
                    );
                } else {
                    return (
                        <Chip
                            icon={<AccessTime />}
                            label={params.row.payout_date}
                            style={{ borderRadius: '4px' }}
                            size="small"
                            color="warning"
                            variant="filled"
                            title="Scheduled to pay out on this date"
                        />
                    );
                }
            },
        },
        {
            field: 'partner',
            headerName: 'Partner',
            minWidth: 250,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: (_: any, row: any) => {
                return row.other_partner?.organization?.name ?? '-';
            },
        },
        {
            field: 'athlete',
            headerName: 'Athlete',
            minWidth: 250,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: (_: any, row: any) => {
                return row.athlete_user?.name ?? '-';
            },
        },
        {
            field: 'sellerPartner',
            headerName: 'Reseller',
            minWidth: 250,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: (_: any, row: any) => {
                return row.seller_partner?.organization?.name ?? '-';
            },
        },
        {
            field: 'reason',
            headerName: 'Reason',
            minWidth: 250,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: (_: any, row: any) => {
                return row.bonus?.description;
            },
        },
    ];

    const drawerColumns: GridColDef[] = [
        {
            field: 'type',
            headerName: 'Earnings Type',
            minWidth: 250,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Box display="flex" alignItems="center">
                        <span>{params.value}</span>
                    </Box>
                );
            },
        },
        {
            field: 'amount',
            headerName: 'Amount',
            minWidth: 100,
            disableColumnMenu: true,
            sortable: true,
            renderCell: (params: GridRenderCellParams<any, number>) => {
                return formatCurrency({
                    amount: ((params.value ?? 0) / 100) as number,
                    currency: 'USD',
                });
            },
        },
        {
            field: 'earned_date',
            headerName: 'Earned On',
            minWidth: 180,
            disableColumnMenu: true,
            sortable: true,
            renderCell: (params: GridRenderCellParams<any, string>) => {
                return formatDateTimeStringToLocale(params.value, 'America/Los_Angeles');
            },
        },
        {
            field: 'is_paid',
            headerName: 'Paid Out',
            minWidth: 50,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params: GridRenderCellParams<any, string>) => {
                return params.value ? 'Yes' : 'No';
            },
        },
        {
            field: 'partner',
            headerName: 'Partner',
            minWidth: 250,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: (_: any, row: any) => {
                return row.other_partner?.organization?.name ?? '-';
            },
        },
        {
            field: 'athlete',
            headerName: 'Athlete',
            minWidth: 250,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: (_: any, row: any) => {
                return row.athlete_user?.name ?? '-';
            },
        },
        {
            field: 'reason',
            headerName: 'Reason',
            minWidth: 250,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: (_: any, row: any) => {
                return row.bonus?.description;
            },
        },
    ];

    const handleSortModelChange = React.useCallback(
        (sortModel: GridSortModel) => {
            dispatch({
                type: NetworkingActionTypes.COMMISSIONS_FILTERS_CHANGED,
                payload: {
                    page: 1,
                    sort: sortModel.map((item: GridSortItem) => {
                        if (item.sort === 'asc') {
                            return `${item.field}`;
                        }
                        return `-${item.field}`;
                    }),
                },
            });
        },
        [dispatch],
    );

    return (
        <Container
            sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
            }}
            maxWidth={'xl'}
        >
            <Stack spacing={4} sx={{ marginTop: '16px' }}>
                <Box
                    sx={{
                        background: `linear-gradient(135deg, #111111 0%, #1A1A1A 50%, #222222 100%)`,
                        borderRadius: 3,
                        p: 4,
                        border: `2px solid rgba(105, 190, 40, 0.3)`,
                        boxShadow: `0 8px 32px rgba(105, 190, 40, 0.1)`,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: `linear-gradient(90deg, #69BE28, #4A8C1C, #69BE28)`,
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative',
                        }}
                    >
                        <img
                            src={FiyrPodLogo}
                            alt="FiyrPod"
                            style={{ height: 36, objectFit: 'contain' }}
                        />
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                color: 'rgba(255,255,255,0.85)',
                                letterSpacing: '-0.5px',
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '100%',
                                textAlign: 'center',
                                pointerEvents: 'none',
                            }}
                        >
                            Partnership Earnings
                        </Typography>
                    </Box>
                </Box>
                <PartnershipCommissionsAnalytics />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mt: 3,
                        mb: 2,
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                        }}
                    >
                        Earnings List
                    </Typography>
                    <Box
                        sx={{
                            flex: 1,
                            height: 3,
                            background: `linear-gradient(90deg, #69BE28 0%, transparent 100%)`,
                            borderRadius: 2,
                        }}
                    />
                </Box>
                {Object.entries(unpaidCommissionsGroupedByPayoutDate).length > 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            mb: 2,
                        }}
                    >
                        {Object.entries(unpaidCommissionsGroupedByPayoutDate).map(
                            ([payoutDate, value]) => (
                                <Chip
                                    key={payoutDate}
                                    icon={<AccessTime />}
                                    label={`${formatCurrency({
                                        amount: value / 100,
                                        currency: 'USD',
                                    })} on ${payoutDate}`}
                                    sx={{
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        py: 2.5,
                                        px: 1.5,
                                        boxShadow: `0 4px 12px ${theme.palette.warning.main}40`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 6px 16px ${theme.palette.warning.main}60`,
                                        },
                                    }}
                                    color="warning"
                                    variant="filled"
                                    title="Total amount scheduled to be paid out on this date"
                                />
                            ),
                        )}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: theme.palette.grey[100],
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            No upcoming payouts scheduled
                        </Typography>
                    </Box>
                )}
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isSmallScreen ? 'column' : 'row',
                            justifyContent: 'space-between',
                            gap: 2,
                            alignItems: isSmallScreen ? 'stretch' : 'center',
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={commissions.filters['filter[is_paid]'] === 0}
                                    onChange={() => {
                                        dispatch({
                                            type: NetworkingActionTypes.COMMISSIONS_FILTERS_CHANGED,
                                            payload: {
                                                page: 1,
                                                'filter[is_paid]':
                                                    commissions.filters['filter[is_paid]'] === 0
                                                        ? undefined
                                                        : 0,
                                            },
                                        });
                                    }}
                                    name="is_paid"
                                    color="primary"
                                    size="medium"
                                />
                            }
                            label={
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    Show Only Unpaid Earnings
                                </Typography>
                            }
                        />
                        <FormControl sx={{ width: isSmallScreen ? '100%' : 300 }} size="small">
                            <InputLabel id="filter-by-type">Filter by Type</InputLabel>
                            <Select
                                label="Filter by Type"
                                variant={'outlined'}
                                value={selectedCommissionType}
                                onChange={handleTypeFilterChange}
                                multiple
                                placeholder={'Filter by Type'}
                                size="small"
                            >
                                {commissionTypes.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ width: isSmallScreen ? '100%' : 200 }} size="small">
                            <InputLabel id="filter-by-month">Filter by Month</InputLabel>
                            <Select
                                labelId="filter-by-month"
                                label="Filter by Month"
                                variant={'outlined'}
                                value={selectedMonth}
                                onChange={handleMonthFilterChange}
                                size="small"
                            >
                                <MenuItem value="">All Months</MenuItem>
                                {monthOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            variant="outlined"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search earnings type, partner or reason"
                            sx={{ width: isSmallScreen ? '100%' : 350 }}
                            size="small"
                        />
                    </Box>
                </Paper>
            </Stack>
            <Box
                sx={{
                    flex: 1,
                    width: '100%',
                    overflow: 'auto',
                }}
            >
                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <DataGridPremium
                        sx={{
                            '& .MuiDataGrid-row:hover': {
                                cursor: 'pointer',
                                backgroundColor: 'rgba(105, 190, 40, 0.08)',
                                transform: 'scale(1.01)',
                                transition: 'all 0.2s ease',
                            },
                            '& .MuiDataGrid-row': {
                                transition: 'all 0.2s ease',
                            },
                            minHeight: '500px',
                        }}
                        loading={commissions.loading}
                        rows={commissions.data}
                        columns={columns}
                        sortingMode={'server'}
                        onSortModelChange={handleSortModelChange}
                        slots={{
                            loadingOverlay: CustomLoadingOverlay,
                            footer: CustomPagination,
                            noRowsOverlay: () => (
                                <Stack height="100%" alignItems="center" justifyContent="center">
                                    <Typography variant="h6">No Results</Typography>
                                </Stack>
                            ),
                            noResultsOverlay: () => (
                                <Stack height="100%" alignItems="center" justifyContent="center">
                                    No Results When Filter Applied
                                </Stack>
                            ),
                        }}
                        getRowId={(row) => row.uuid}
                        slotProps={{
                            footer: {
                                hasNext: commissions.pagination.has_next,
                                hasPrevious: commissions.pagination.has_prev,
                                onNext: () => {
                                    dispatch({
                                        type: NetworkingActionTypes.COMMISSIONS_FILTERS_CHANGED,
                                        payload: {
                                            page: commissions.pagination.current_page + 1,
                                        },
                                    });
                                },
                                onPrevious: () => {
                                    dispatch({
                                        type: NetworkingActionTypes.COMMISSIONS_FILTERS_CHANGED,
                                        payload: {
                                            page: commissions.pagination.current_page - 1,
                                        },
                                    });
                                },
                                page: commissions.pagination.current_page,
                            },
                        }}
                    />
                </Paper>
            </Box>
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={Boolean(commissions.errorMessage)}
                message={commissions.errorMessage}
                onClose={() => {
                    dispatch({
                        type: NetworkingActionTypes.COMMISSIONS_ERROR_MESSAGE_CLEARED,
                    });
                }}
                autoHideDuration={3000}
            />
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                    setDrawerCommissions([]);
                }}
                sx={{ width: '75%' }} // Set this to your preferred width
            >
                <Box sx={{ width: '100%', height: 400 }}>
                    <DataGridPremium
                        rows={drawerCommissions}
                        columns={drawerColumns}
                        sx={{ width: '100%', height: '100%' }}
                        getRowId={(row) => row.uuid}
                    />
                </Box>
            </Drawer>
            <OrderView open={orderViewOpen} order={selectedOrder} onClose={handleCloseOrderView} />
        </Container>
    );
};
export default PartnershipEarningsScene;
